import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import SiteLogo from "@/components/SiteLogo";
import { Eye, EyeOff, KeyRound, Loader2, Sparkles } from "lucide-react";

const copy = {
  ro: {
    badge: "Mentenanță",
    accessTitle: "Acces echipă",
    accessHint: "Ai primit o parolă de previzualizare? Introdu-o mai jos pentru a vedea site-ul.",
    passwordPlaceholder: "Parola de acces",
    submit: "Intră pe site",
    submitting: "Se verifică…",
    success: "Acces acordat — te redirecționăm…",
    toggleShow: "Arată parola",
    toggleHide: "Ascunde parola",
  },
  en: {
    badge: "Maintenance",
    accessTitle: "Team access",
    accessHint: "Received a preview password? Enter it below to view the site.",
    passwordPlaceholder: "Access password",
    submit: "Enter site",
    submitting: "Checking…",
    success: "Access granted — redirecting…",
    toggleShow: "Show password",
    toggleHide: "Hide password",
  },
};

/**
 * Full-screen maintenance page for public visitors (not shown under /admin).
 */
export default function MaintenanceScreen({ settings, onBypass }) {
  const { lang } = useLanguage();
  const t = copy[lang] || copy.ro;
  const headline = lang === "en" ? settings.headline_en : settings.headline_ro;
  const bodyText = lang === "en" ? settings.body_en : settings.body_ro;
  const showBypass = Boolean(settings.has_bypass_password);

  const [pulse, setPulse] = useState(true);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setPulse((p) => !p), 2800);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const r = await fetch("/api/maintenance-status.php", { cache: "no-store" });
        const j = await r.json();
        if (j.ok && !j.enabled) window.location.reload();
      } catch {
        /* ignore */
      }
    }, 12000);
    return () => clearInterval(id);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !password.trim()) return;

    setSubmitting(true);
    setError("");

    try {
      const r = await fetch("/api/maintenance-bypass.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password: password.trim() }),
      });
      const j = await r.json().catch(() => ({}));

      if (!r.ok || !j.ok) {
        setError(j.detail || (lang === "en" ? "Incorrect password." : "Parolă incorectă."));
        return;
      }

      setSuccess(true);
      window.setTimeout(() => {
        if (typeof onBypass === "function") {
          onBypass();
        } else {
          window.location.reload();
        }
      }, 700);
    } catch {
      setError(lang === "en" ? "Network error. Try again." : "Eroare de rețea. Încearcă din nou.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[100svh] overflow-x-clip overflow-y-auto bg-[#1a1210] text-[#fbd9e5]">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(251,217,229,0.35), transparent 55%), radial-gradient(ellipse 60% 40% at 100% 50%, rgba(183,110,121,0.12), transparent 50%), radial-gradient(ellipse 50% 35% at 0% 80%, rgba(251,217,229,0.08), transparent 45%)",
        }}
      />
      <div className="pointer-events-none absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.03\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/svg%3E')]" />

      <div className="relative z-10 mx-auto flex min-h-[100svh] w-full max-w-lg flex-col items-center justify-center px-5 py-10 text-center sm:px-6 sm:py-16">
        <div className="mb-8 inline-flex items-center justify-center rounded-[2rem] border border-white/10 bg-white/[0.06] px-7 py-5 shadow-[0_20px_60px_rgba(0,0,0,0.35)] backdrop-blur-md sm:mb-10 sm:px-8 sm:py-6">
          <SiteLogo variant="header" />
        </div>

        <div
          className={`mb-5 inline-flex items-center gap-2 rounded-full border border-[#fbd9e5]/25 bg-[#fbd9e5]/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-[#fbd9e5]/90 transition-opacity duration-1000 sm:mb-6 sm:text-[11px] ${pulse ? "opacity-100" : "opacity-75"}`}
        >
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          {t.badge}
        </div>

        <h1 className="font-display text-balance text-[1.75rem] font-black leading-tight tracking-tight text-white sm:text-4xl">
          {headline}
        </h1>

        <div className="mx-auto mt-6 h-px w-20 bg-gradient-to-r from-transparent via-[#fbd9e5]/50 to-transparent sm:mt-8 sm:w-24" aria-hidden />

        <p className="mt-6 max-w-md text-pretty text-[15px] leading-relaxed text-[#fbd9e5]/78 sm:mt-8 sm:text-base">
          {bodyText}
        </p>

        <div className="mt-8 rounded-2xl border border-white/10 bg-black/20 px-5 py-3.5 text-sm text-[#fbd9e5]/55 backdrop-blur-sm sm:mt-10 sm:px-6 sm:py-4">
          <p className="font-semibold text-[#fbd9e5]/75">Social Destiny</p>
          <p className="mt-1 text-xs text-[#fbd9e5]/45">
            {lang === "en" ? "Digital agency · Romania" : "Agenție digitală · România"}
          </p>
        </div>

        {showBypass ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 w-full max-w-md text-left sm:mt-12"
          >
            <div className="overflow-hidden rounded-[1.5rem] border border-[#fbd9e5]/15 bg-white/[0.04] shadow-[0_24px_70px_-24px_rgba(0,0,0,0.55)] backdrop-blur-xl">
              <div className="border-b border-[#fbd9e5]/10 px-5 py-4 sm:px-6">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fbd9e5]/15 text-[#fbd9e5]">
                    <KeyRound className="h-4 w-4" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="font-display text-base font-bold text-white sm:text-[17px]">{t.accessTitle}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-[#fbd9e5]/60 sm:text-[13px]">{t.accessHint}</p>
                  </div>
                </div>
              </div>

              <form onSubmit={onSubmit} className="space-y-4 px-5 py-5 sm:px-6 sm:py-6">
                <div className="relative">
                  <label htmlFor="maint-bypass-password" className="sr-only">
                    {t.passwordPlaceholder}
                  </label>
                  <input
                    id="maint-bypass-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError("");
                    }}
                    autoComplete="current-password"
                    placeholder={t.passwordPlaceholder}
                    disabled={submitting || success}
                    className="h-12 w-full rounded-xl border border-white/10 bg-black/25 pr-12 pl-4 text-sm text-white placeholder:text-[#fbd9e5]/35 outline-none ring-[#fbd9e5]/30 transition focus:border-[#fbd9e5]/35 focus:ring-2 disabled:opacity-60"
                    data-testid="maintenance-bypass-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute top-1/2 right-2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-[#fbd9e5]/55 transition hover:bg-white/5 hover:text-[#fbd9e5]"
                    aria-label={showPassword ? t.toggleHide : t.toggleShow}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                <AnimatePresence mode="wait">
                  {error ? (
                    <motion.p
                      key="error"
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="rounded-xl border border-rose-400/25 bg-rose-500/10 px-3 py-2.5 text-xs font-medium text-rose-100/90"
                      role="alert"
                    >
                      {error}
                    </motion.p>
                  ) : null}
                  {success ? (
                    <motion.p
                      key="success"
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-3 py-2.5 text-xs font-medium text-emerald-100/90"
                    >
                      {t.success}
                    </motion.p>
                  ) : null}
                </AnimatePresence>

                <button
                  type="submit"
                  disabled={submitting || success || !password.trim()}
                  className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#fbd9e5] px-5 text-sm font-semibold text-[#3e2723] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="maintenance-bypass-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      {t.submitting}
                    </>
                  ) : (
                    t.submit
                  )}
                </button>
              </form>
            </div>
          </motion.div>
        ) : null}
      </div>
    </div>
  );
}
